// Inventory-modal tooltip rendering — extracted from inventory-modal.ts
// to keep that file under §10's 400-line cap.
//
// Pure DOM construction over a structured TooltipLine union; the modal
// owns the show/hide/move lifecycle.

import { balance } from "./types.ts";
import { getItemDef } from "../../shared/items.ts";
import type { InventorySlot } from "../../shared/messages.ts";
import type { EquippedItemReadout } from "../../shared/equip-messages.ts";
import { parseItemId, QUALITY_COLORS, QUALITY_LABELS, type QualityTier } from "../../shared/quality.ts";
import { getRecipeByOutputId } from "../../shared/recipes.ts";

const TIERS = balance.skills.tierThresholds;

export type TooltipLine =
  | { kind: "title"; text: string; color: string }
  | { kind: "tag"; text: string; color: string }
  | { kind: "skill"; skillId: string; tier: string }
  | { kind: "bar"; current: number; max: number };

function tierLabelFor(xp: number): string {
  if (xp >= TIERS.master.min) return TIERS.master.label;
  if (xp >= TIERS.expert.min) return TIERS.expert.label;
  if (xp >= TIERS.journeyman.min) return TIERS.journeyman.label;
  if (xp >= TIERS.apprentice.min) return TIERS.apprentice.label;
  return TIERS.novice.label;
}

function colorToHex(color: number): string {
  return "#" + color.toString(16).padStart(6, "0"); // contracts:allow-literal — hex string formatting
}

export function buildInventoryTooltipLines(
  slot: InventorySlot,
  getSkillXp: (skillId: string) => number,
): TooltipLine[] {
  const itemDef = getItemDef(slot.itemId);
  if (itemDef === undefined) return [];
  const { baseId, quality } = parseItemId(slot.itemId);
  return assembleLines(itemDef.name, quality, slot, baseId, getSkillXp);
}

export function buildEquippedTooltipLines(equipped: EquippedItemReadout): TooltipLine[] {
  const itemDef = getItemDef(equipped.itemId);
  if (itemDef === undefined) return [];
  const lines: TooltipLine[] = [
    { kind: "title", text: itemDef.name, color: "#eef" },
    { kind: "tag", text: "Equipped", color: "#88ff88" },
  ];
  if (equipped.durabilityMax > 0) {
    lines.push({ kind: "bar", current: equipped.durabilityCurrent, max: equipped.durabilityMax });
  }
  return lines;
}

function assembleLines(
  name: string,
  quality: QualityTier | null,
  slot: InventorySlot,
  baseId: string,
  getSkillXp: (skillId: string) => number,
): TooltipLine[] {
  const lines: TooltipLine[] = [];
  const titleColor = quality !== null ? colorToHex(QUALITY_COLORS[quality]) : "#eef";
  const titleText = quality !== null ? `${QUALITY_LABELS[quality]} ${name}` : name;
  lines.push({ kind: "title", text: titleText, color: titleColor });
  if (slot.durabilityMax !== undefined && slot.durabilityCurrent !== undefined && slot.durabilityMax > 0) {
    lines.push({ kind: "bar", current: slot.durabilityCurrent, max: slot.durabilityMax });
  }
  const recipe = getRecipeByOutputId(baseId);
  if (recipe && recipe.skillGates.length > 0) {
    for (const gate of recipe.skillGates) {
      lines.push({ kind: "skill", skillId: gate.skillId, tier: tierLabelFor(getSkillXp(gate.skillId)) });
    }
  }
  if (slot.count > 1) {
    lines.push({ kind: "tag", text: `x${slot.count}`, color: "#aab" });
  }
  return lines;
}

export function renderTooltipElement(lines: TooltipLine[]): HTMLDivElement {
  const tt = document.createElement("div");
  tt.id = "inventory-tooltip";
  tt.style.cssText =
    "position:fixed;background:#0a0a1c;border:1px solid #4455cc;border-radius:3px;" +
    "padding:6px 10px;font-family:monospace;font-size:11px;color:#ccd;" +
    "z-index:9400;pointer-events:none;max-width:240px;white-space:nowrap;" +
    "box-shadow:0 4px 10px rgba(0,0,0,0.5)";
  for (const line of lines) tt.appendChild(renderTooltipLine(line));
  return tt;
}

function renderTooltipLine(line: TooltipLine): HTMLDivElement {
  const div = document.createElement("div");
  div.style.cssText = "margin:1px 0";
  if (line.kind === "title") {
    div.dataset.tooltipTitle = "1";
    div.style.color = line.color;
    div.style.fontWeight = "bold";
    div.textContent = line.text;
  } else if (line.kind === "tag") {
    div.dataset.tooltipTag = "1";
    div.style.color = line.color;
    div.textContent = line.text;
  } else if (line.kind === "skill") {
    div.dataset.tooltipSkill = "1";
    div.style.color = "#aab";
    div.textContent = `${line.skillId}: ${line.tier}`;
  } else {
    div.dataset.tooltipBar = "1";
    div.appendChild(renderDurabilityBar(line.current, line.max));
  }
  return div;
}

function renderDurabilityBar(current: number, max: number): HTMLDivElement {
  const wrap = document.createElement("div");
  wrap.style.cssText = "display:flex;align-items:center;gap:6px";
  const barBg = document.createElement("div");
  barBg.style.cssText = "width:120px;height:6px;background:#222244;border-radius:2px;overflow:hidden";
  const ratio = Math.max(0, Math.min(1, max > 0 ? current / max : 0));
  const fill = document.createElement("div");
  const fillColor = current === 0 ? "#cc4444" : ratio < 0.4 ? "#ddaa44" : "#55cc77"; // contracts:allow-literal — visual threshold
  fill.style.cssText = `width:${Math.round(ratio * 100)}%;height:100%;background:${fillColor}`; // contracts:allow-literal — percent unit
  barBg.appendChild(fill);
  wrap.appendChild(barBg);
  const num = document.createElement("span");
  num.textContent = `${current}/${max}`;
  num.style.color = "#aab";
  wrap.appendChild(num);
  return wrap;
}

export function moveTooltipElement(el: HTMLDivElement, x: number, y: number): void {
  const offset = 14; // contracts:allow-literal — pixel offset from cursor
  const rect = el.getBoundingClientRect();
  const tx = Math.min(window.innerWidth - rect.width - 4, x + offset); // contracts:allow-literal — 4px edge margin
  const ty = Math.max(4, Math.min(window.innerHeight - rect.height - 4, y + offset)); // contracts:allow-literal — 4px edge margin
  el.style.left = `${tx}px`;
  el.style.top = `${ty}px`;
}
